import { gql, useMutation } from '@apollo/client';
import React from 'react';
import UIActions from '../ui/UIActions';
import topicPath from '../utils/url/topicPath';
import { useRouter } from '../utils/router/v3Bridge';
import useAllTopics from './useAllTopics';
import { useAppDispatch } from '../app/redux/useAppSelector';

const DELETE_TOPIC_MUTATION = gql`
  mutation deleteTopic($id: ID!) {
    deleteTopic(id: $id) {
      id
    }
  }
`;

const useDeleteTopic = (id: string) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const allTopics = useAllTopics({ flatten: true });
  const [mutate] = useMutation(DELETE_TOPIC_MUTATION, { variables: { id } });

  return () => {
    const topic = allTopics.find((t) => t.id === id);
    if (!topic) return Promise.resolve();

    const {
      ancestors: [parent],
      childTopics,
      name,
      privacy,
    } = topic;

    return new Promise((resolve, reject) =>
      // eslint-disable-next-line no-promise-executor-return -- Fix next time editing the file
      dispatch(
        UIActions.confirmModal({
          title: 'Delete this topic?',
          message: (
            <>
              <p>Deleted topics are gone forever.</p>
              {childTopics.length > 0 && (
                <p>
                  <strong>{name}</strong>&apos;s subtopics will{' '}
                  {parent ? (
                    <>
                      be moved to <strong>{parent.name}</strong>.
                    </>
                  ) : (
                    'become top level topics.'
                  )}
                </p>
              )}
              {privacy === 'PRIVATE' && (
                <p>
                  Posts that are uncategorized after the deletion will be
                  accessible by anyone.
                </p>
              )}
            </>
          ),
          confirmButtonText: 'Delete',
          onConfirm: () => {
            mutate({
              variables: { id },
              optimisticResponse: {
                __typename: 'Mutation',
                deleteTopic: {
                  __typename: 'Topic',
                  id,
                },
              },
            })
              .then(resolve)
              .catch(reject);

            router.push(parent ? topicPath(parent) : '/');
          },
        }),
      ),
    );
  };
};

export default useDeleteTopic;
